/**
 * Katalog ekranlarının veri kaynağı: periyodik tazelenen kütüphane listesi.
 *
 * `GET /api/libraries` her kütüphane için `reachable` + `unavailableReason`
 * döner (ADR-011). Sunucu bu değeri 30 sn önbellekler; istemci de aynı aralıkla
 * yoklar, böylece tünel düştüğünde ekran kendiliğinden tazelenir.
 *
 * Kütüphaneler arasında geçiş yapılırken de liste tazelenir; böylece "Ev
 * Arşivi"ye dönüldüğünde eski erişilebilirlik bilgisi gösterilmez.
 */
import { useMemo } from 'react';
import type { Library } from './api.ts';
import { api } from './api.ts';
import { type PolledValue, usePolledValue } from './usePolledValue.ts';

export function useLibraries(): PolledValue<Library[]> {
  const polled = usePolledValue(() => api.libraries());

  const value = useMemo(() => (polled.value === null ? null : [...polled.value]), [polled.value]);

  return { ...polled, value };
}

/** Tek bir kütüphanenin erişilebilirliği; listeden okunur. */
export function useLibrary(libraryId: string | null): {
  library: Library | null;
  loading: boolean;
  refresh: () => void;
} {
  const { value, loading, refresh } = useLibraries();
  const library = value?.find((entry) => entry.id === libraryId) ?? null;
  return { library, loading, refresh };
}

/** Site geneli uyarı için: erişilemeyen kütüphanelerin listesi. */
export function unreachableOf(libraries: Library[] | null): Library[] {
  return (libraries ?? []).filter((library) => !library.reachable);
}

/** Kullanıcıya gösterilecek tek satırlık özet. */
export function unreachableSummary(libraries: Library[] | null): string | null {
  const down = unreachableOf(libraries);
  if (down.length === 0) return null;
  return down
    .map((library) => `${library.name} (${library.unavailableReason ?? 'erişilemiyor'})`)
    .join(', ');
}

export type { PolledValue };
